import React, { ReactElement, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { SharingNewDropdown } from 'reactApp/components/SharingNew/Dropdown/SharingNewDropdown';
import { EDropdownColors } from 'reactApp/components/SharingNew/Dropdown/SharingNewDropdown.types';
import { TOOLTIP_OFFSET } from 'reactApp/components/SharingNew/SharingNew.constants';
import { sendGa } from 'reactApp/components/SharingNew/SharingNew.helpers';
import { EAccessRights, IAccessRightsOptions } from 'reactApp/components/SharingNew/SharingNew.types';
import { deleteUser, setAccessRight } from 'reactApp/modules/folderAccessControlList/folderAccessControlList.actions';
import { unmountRequest } from 'reactApp/modules/modifying/modifying.actions';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { ACCESS_RIGHTS_OPTIONS, DELETE_USER, UNMOUNT_USER } from './User.constants';
import styles from './User.css';
import { renderDeleteUserDialog } from './User.helpers';

export const useAccessRights = ({ user, item, isMounted, onClose, isPhone, isOwner, publicId }): ReactElement | null | string => {
    const dispatch = useDispatch();
    const userRight = ACCESS_RIGHTS_OPTIONS[user.accessRights];

    const onActionClick = useCallback(() => {
        if (!user || !item) {
            return;
        }
        sendGa('delete-user');
        sendPaymentGa({
            eventCategory: publicId ? ECategoryGa.public : ECategoryGa.sharedFolder,
            action: 'shutdown-sharing',
            public_id: publicId,
        });
        dispatch(deleteUser({ email: user.email, item }));
    }, [dispatch, item, publicId, user]);

    const handleAccessRightsUpdate = useCallback((accessRight): void => {
        if (!user) {
            return;
        }

        if (accessRight === DELETE_USER.key) {
            renderDeleteUserDialog({ user, onActionClick });
            return;
        }

        sendGa('user-rights-change');
        dispatch(setAccessRight({ email: user.email, accessRight, folderId: item?.id }));
    }, [dispatch, item?.id, onActionClick, user]);

    const onUnmount = useCallback(() => {
        sendGa('unmount');
        onClose();
    }, [onClose]);

    const handleOpenUnmountDialog = useCallback(() => {
        dispatch(unmountRequest({ item, onUnmount }));
    }, [item, onUnmount]);

    const renderAccessRightsDropdown = useCallback((onClick): ReactElement | null => {
        const values = {
            ...ACCESS_RIGHTS_OPTIONS,
            [EAccessRights.DELETE_USER]: DELETE_USER,
        };

        const list = Object.values(values).map((item: IAccessRightsOptions) => ({
            id: item.key,
            text: item.text,
            check: item.key === user?.accessRights,
            qaValue: item.key,
        }));

        return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
    }, [user]);

    if (isOwner) {
        return <div className={styles.owner}>Владелец папки</div>;
    }

    if (isMounted) {
        return (
            <div className={styles.unmount} onClick={handleOpenUnmountDialog} data-qa-value={UNMOUNT_USER.key}>
                <div className={styles.unmountText}>
                    {UNMOUNT_USER.text}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.dropdown}>
            <Dropdown
                withinDialog
                theme="octavius"
                value={userRight?.text}
                qaValue={userRight?.key}
                as={SharingNewDropdown}
                color={isPhone ? EDropdownColors.BLUE : EDropdownColors.GRAY}
                placement={tooltipPlacements.BOTTOM_LEFT}
                content={renderAccessRightsDropdown}
                onClickItem={handleAccessRightsUpdate}
                tooltipOffset={TOOLTIP_OFFSET}
                animatedTooltip
                isPhone={isPhone}
                isUserDropdown
                tooltipClassName={isPhone ? styles.tooltip : undefined}
            />
        </div>
    );
};
